import type {
  AchievementAward,
  Attempt,
  FactId,
  FactMastery,
  LearningObjective,
  ResponseMode,
  SelectionReason,
  SessionBucket,
  SkillId,
  SkillMastery,
  StrategyEvidence,
  StrategyId,
  StrategyLessonResponse,
  StrategyMastery,
  SupportLevel,
  SchedulingState,
  StrategyLessonInstance,
  LessonActivityResponse,
} from "@oefenheld/reken-core";

export const PROFILE_SCHEMA_VERSION = 1;

export type SupportedNumberRange = 20 | 30 | 50 | 100;
export type SessionMode = "SHORT" | "NORMAL" | "CHALLENGE";
export type SessionPhase =
  | "CONFIGURED"
  | "CORE_ACTIVE"
  | "CORE_COMPLETE"
  | "REVEAL_PENDING"
  | "BONUS_ACTIVE"
  | "COMPLETE"
  | "EARLY_EXIT";

export interface LearnerProfile {
  readonly id: string;
  readonly displayName: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly timeZone: string;
  readonly maximumAllowedRange: SupportedNumberRange;
  readonly currentAdaptiveRange: SupportedNumberRange;
  readonly schemaVersion: typeof PROFILE_SCHEMA_VERSION;
}

export interface SessionRecord {
  readonly completionReason?: "DONE_FOR_NOW" | "CONTENT_GAP";
  readonly completionTrace?: readonly string[];
  readonly id: string;
  readonly profileId: string;
  readonly mode: SessionMode;
  readonly phase: SessionPhase;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly localDate: string;
  readonly timeZone: string;
  readonly voluntarilyStarted: boolean;
  readonly coreAttemptCount: number;
  readonly bonusAttemptCount: number;
  readonly bucketCounts: Readonly<Record<SessionBucket, number>>;
  readonly consecutiveDifficultOutcomes: number;
  readonly strategyIntroductionCount: number;
  readonly helpCount: number;
  readonly errorCount: number;
  readonly bonusRoundOffered: boolean;
  readonly bonusRoundAccepted: boolean;
  readonly earlyExit: boolean;
  readonly achievementUnlockIds: readonly string[];
  readonly catalogVersion: string;
  readonly rulesetVersion: string;
}

export interface ItemPresentation {
  readonly recovery?: { readonly reason: "SESSION_INACTIVE" | "SESSION_MISSING" | "RESULT_ALREADY_RECORDED"; readonly at: string };
  readonly lesson?: StrategyLessonInstance;
  readonly lessonProgress?: LessonProgress;
  readonly schedulingVersion?: string;
  readonly id: string;
  readonly profileId: string;
  readonly sessionId: string;
  readonly factId: FactId | null;
  readonly targetSkillId: SkillId;
  readonly strategyId: StrategyId | null;
  readonly selectionReason: SelectionReason;
  readonly presentedAt: string;
  readonly resolvedAt: string | null;
  readonly state: "PRESENTED" | "RESOLVED" | "ABANDONED";
  readonly lessonResponse?: StrategyLessonResponse;
  readonly selectionDecisionId: string;
  readonly bucket: SessionBucket;
}

export interface LessonProgress {
  readonly activityIndex: number;
  readonly questionIndex: number;
  readonly answers: Readonly<Record<string, Readonly<Record<string, number>>>>;
  readonly responses: readonly LessonActivityResponse[];
}

export interface SchedulingCommit {
  readonly expectedRevision: number;
  readonly state: SchedulingState;
}

export interface SelectorDecisionRecord {
  readonly id: string;
  readonly profileId: string;
  readonly sessionId: string;
  readonly createdAt: string;
  readonly reason: SelectionReason;
  readonly factId: FactId | null;
  readonly targetSkillId: SkillId;
  readonly strategyId: StrategyId | null;
  readonly objective: LearningObjective;
  readonly responseMode: ResponseMode;
  readonly supportLevel: SupportLevel;
  readonly seed: string;
  readonly trace: readonly string[];
  readonly bucket: SessionBucket;
}

export type AchievementUnlock = AchievementAward;

export interface StoredFactMastery extends FactMastery {
  readonly key: string;
  readonly profileId: string;
  readonly updatedAt: string;
}

export interface StoredStrategyMastery extends StrategyMastery {
  readonly key: string;
  readonly profileId: string;
  readonly updatedAt: string;
}

export interface StoredSkillMastery extends SkillMastery {
  readonly key: string;
  readonly profileId: string;
  readonly updatedAt: string;
}

export interface LegacyImportRecord {
  readonly id: string;
  readonly profileId: string;
  readonly sourceApp: "TIJDHELD" | "TAFELHELD";
  readonly sourceOrigin: string;
  readonly importedAt: string;
  readonly sourceSchemaVersion: string;
  readonly payloadHash: string;
  readonly status: "IMPORTED_AS_LEGACY" | "SKIPPED" | "FAILED";
  readonly note: string;
}

export interface LearningCommit {
  readonly scheduling?: SchedulingCommit;
  readonly attempt: Attempt;
  readonly presentation: ItemPresentation;
  readonly factMastery: StoredFactMastery;
  readonly strategyEvidence: readonly StrategyEvidence[];
  readonly strategyMastery: readonly StoredStrategyMastery[];
  readonly skillMastery: readonly StoredSkillMastery[];
  readonly achievementUnlocks: readonly AchievementUnlock[];
  readonly session: SessionRecord;
}

export interface StrategyLearningCommit {
  readonly scheduling?: SchedulingCommit;
  readonly presentation: ItemPresentation;
  readonly strategyEvidence: readonly StrategyEvidence[];
  readonly strategyMastery: StoredStrategyMastery;
  readonly skillMastery: readonly StoredSkillMastery[];
  readonly achievementUnlocks: readonly AchievementUnlock[];
  readonly session: SessionRecord;
}
